import { useEffect, useState } from "react";
import { Building2, Plus, Trash2, Pencil, Users, Briefcase } from "lucide-react";
import {
  listOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const emptyForm = {
  name: "",
  cnpj: "",
  admin_email: "",
  admin_password: "",
  admin_name: "",
};

export default function Organizations() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      setOrgs(await listOrganizations());
    } catch {
      toast.error("Erro ao carregar escritórios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (o) => {
    setEditing(o);
    setForm({ ...emptyForm, name: o.name, cnpj: o.cnpj || "" });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateOrganization(editing.id, { name: form.name, cnpj: form.cnpj });
        toast.success("Escritório atualizado");
      } else {
        if (!form.name || !form.admin_email || !form.admin_password || !form.admin_name) {
          toast.error("Preencha todos os campos obrigatórios");
          return;
        }
        await createOrganization(form);
        toast.success("Escritório criado com sucesso");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteOrganization(confirmDelete.id);
      toast.success("Escritório excluído (todos os dados removidos)");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
  };

  return (
    <div className="space-y-6" data-testid="organizations-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]">
            Escritórios
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">
            Gerencie os escritórios de contabilidade cadastrados na plataforma.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNew}
              data-testid="add-organization-button"
              className="bg-[#0F2942] hover:bg-[#173E63] text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Escritório
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar Escritório" : "Novo Escritório"}</DialogTitle>
              <DialogDescription>
                {editing
                  ? "Atualize os dados cadastrais."
                  : "Ao criar, um administrador inicial será provisionado."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Nome do Escritório *</Label>
                <Input
                  data-testid="org-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1"
                  placeholder="Ex: Contabilidade Silva & Cia"
                />
              </div>
              <div>
                <Label>CNPJ</Label>
                <Input
                  data-testid="org-cnpj-input"
                  value={form.cnpj}
                  onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                  className="mt-1"
                  placeholder="Opcional"
                />
              </div>
              {!editing && (
                <div className="pt-3 border-t border-slate-200 space-y-4">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    Administrador Inicial
                  </p>
                  <div>
                    <Label>Nome do Admin *</Label>
                    <Input
                      data-testid="org-admin-name-input"
                      value={form.admin_name}
                      onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>E-mail do Admin *</Label>
                    <Input
                      type="email"
                      data-testid="org-admin-email-input"
                      value={form.admin_email}
                      onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Senha do Admin *</Label>
                    <Input
                      type="password"
                      data-testid="org-admin-password-input"
                      value={form.admin_password}
                      onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                      placeholder="Mínimo 6 caracteres"
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="org-submit-button"
                  className="bg-[#0F2942] hover:bg-[#173E63] text-white"
                >
                  {editing ? "Salvar" : "Criar Escritório"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm">Carregando...</div>
        ) : orgs.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">
            Nenhum escritório cadastrado ainda.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="organizations-list">
            {orgs.map((o) => (
              <li
                key={o.id}
                className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-slate-50 transition-colors"
              >
                <div className="h-10 w-10 rounded-lg bg-[#0F2942] text-white flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#0F172A]">{o.name}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {o.cnpj ? `CNPJ: ${o.cnpj}` : "Sem CNPJ"}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-600">
                  <span className="flex items-center gap-1" title="Usuários">
                    <Users className="h-3.5 w-3.5" /> {o.users_count}
                  </span>
                  <span className="flex items-center gap-1" title="Clientes">
                    <Briefcase className="h-3.5 w-3.5" /> {o.clients_count}
                  </span>
                </div>
                <Badge
                  className={
                    o.active
                      ? "bg-green-100 text-green-700 hover:bg-green-100 border-0"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-100 border-0"
                  }
                >
                  {o.active ? "Ativo" : "Inativo"}
                </Badge>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(o)}
                    data-testid={`edit-org-${o.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmDelete(o)}
                    data-testid={`delete-org-${o.id}`}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir escritório?</AlertDialogTitle>
            <AlertDialogDescription>
              Excluir <b>{confirmDelete?.name}</b> irá remover <b>TODOS</b> os dados vinculados
              (usuários, clientes, prazos, tarefas, fichas e histórico). Esta ação é irreversível.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="confirm-delete-org"
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
